import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useCardHeights — tracks the rendered height of each card element.
 *
 * Uses ResizeObserver so heights stay accurate when cards expand/collapse.
 *
 * IMPORTANT: Heights are updated ONLY via the ResizeObserver callback — never
 * synchronously inside the registerRef call. This prevents infinite re-render
 * loops caused by inline `ref={el => registerRef(id, el)}` patterns (React calls
 * the ref callback on every render when the function reference changes, and a
 * synchronous setState inside that callback would immediately cause another render).
 *
 * Returns:
 *   registerRef(id, el) — stable callback; pass as ref or call manually
 *   heights: { [id]: px }  — live rendered heights
 */
export function useCardHeights() {
  const [heights, setHeights] = useState({});
  const observersRef = useRef({});   // id → ResizeObserver
  const elementsRef = useRef({});    // id → DOM element

  const registerRef = useCallback((id, el) => {
    // Disconnect any existing observer for this id
    if (observersRef.current[id]) {
      observersRef.current[id].disconnect();
      delete observersRef.current[id];
      delete elementsRef.current[id];
    }

    if (!el) return;

    // If this exact element is already being tracked, nothing to do
    // (guards against React calling the callback twice with the same element)
    if (elementsRef.current[id] === el) return;

    elementsRef.current[id] = el;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.borderBoxSize
          ? entry.borderBoxSize[0].blockSize
          : entry.contentRect.height;
        if (h > 0) {
          setHeights(prev => {
            if (prev[id] === h) return prev; // bail out if unchanged → no re-render
            return { ...prev, [id]: h };
          });
        }
      }
    });

    ro.observe(el);
    observersRef.current[id] = ro;
    // No synchronous setHeights here — ResizeObserver fires asynchronously
    // (outside React's commit phase) and will update heights on its own.
  }, []);

  useEffect(() => {
    return () => {
      Object.values(observersRef.current).forEach(ro => ro.disconnect());
    };
  }, []);

  return { registerRef, heights };
}
